import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiFilter } from 'react-icons/fi';
import { supabase } from '../../supabase/supabase';
import { useAuth } from '../../contexts/AuthContext';
import wintoCoin from '../../assets/images/winto-coin.png';
import './Transaction.css';
import FloatingSidebarButton from "../../components/FloatingSidebarButton/FloatingSidebarButton";
import { useLanguage } from "../../contexts/LanguageContext";


// ---------- helpers ----------
const asNumber = (v) => {
  const n = typeof v === 'string' ? parseFloat(v) : Number(v);
  return Number.isFinite(n) ? n : 0;
};

const toDate = (ts) => {
  if (!ts) return null;
  if (typeof ts?.toDate === 'function') return ts.toDate();
  const d = new Date(ts);
  return isNaN(d.getTime()) ? null : d;
};

const formatDateTime = (timestamp) => {
  const d = toDate(timestamp);
  if (!d) return '';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${day}-${month}-${year} @ ${hours}:${minutes}`;
};

const Transaction = () => {
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [docs, setDocs] = useState([]);
  const { translations: t } = useLanguage();


  // Fetch transactions from Supabase transactions table
  useEffect(() => {
    // Mark wallet as verified when entering transaction page
    sessionStorage.setItem('walletVerified', 'true');
    
    if (!currentUser?.id) {
      setDocs([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    // Fetch initial transactions
    const fetchTransactions = async () => {
      try {
        const { data, error } = await supabase
          .from('transactions')
          .select('*')
          .eq('user_id', currentUser.id)
          .eq('field', 'winicoins')
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Error fetching transactions:', error);
          setDocs([]);
          setLoading(false);
          return;
        }

        const rows = (data || []).map((tx) => ({
          id: tx.id,
          path: `transactions/${tx.user_id}/${tx.id}`, // For deduplication
          ...tx,
        }));

        setDocs(rows);
        setLoading(false);
      } catch (err) {
        console.error('Failed to fetch transactions:', err);
        setDocs([]);
        setLoading(false);
      }
    };

    fetchTransactions();

    // Set up real-time subscription
    const channel = supabase
      .channel('transactions-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'transactions',
          filter: `user_id=eq.${currentUser.id}`
        },
        (payload) => {
          console.log('Transaction change received:', payload);
          if (payload.new && payload.new.field === 'winicoins') {
            // Refetch transactions when changes occur
            fetchTransactions();
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [currentUser?.id]);

  // Build display rows: ONLY my transactions, no duplicates, sign derived by me
  const transactions = useMemo(() => {
    if (!currentUser?.id) return [];

    // Deduplicate by full path just in case
    const map = new Map();
    for (const d of docs) map.set(d.path || d.id, d);

    const arr = Array.from(map.values()).map((tx) => {
      // Determine if this is an outgoing transaction
      const isOutgoing = tx.flow === 'out' || tx.sender_id === currentUser.id;
      
      // Get amount (already positive in Supabase)
      const magnitude = Math.abs(asNumber(tx.amount));

      return {
        id: tx.id,
        source: tx.source || (t.trans?.walletSource || "wallet"),
        time: tx.created_at || tx.time, // Use created_at from Supabase
        isOutgoing: isOutgoing,
        amount: magnitude,
      };
    });

    // newest first
    arr.sort((a, b) => {
      const da = toDate(a.time)?.getTime() ?? 0;
      const db = toDate(b.time)?.getTime() ?? 0;
      return db - da;
    });

    return arr;
  }, [docs, currentUser?.id, t]);

  if (loading) {
    return (
      <div className="transaction-page">
              <div className="transaction-loading">{t.trans?.loading || "Loading..."}</div>
      </div>
    );
  }

  return (
    <div className="transaction-page">
      <FloatingSidebarButton userProfile={userProfile} />
      {/* Header Bar */}
      <div className="transaction-header">
        <button
          className="transaction-back"
          onClick={() => navigate(-1)}
          aria-label={t.trans?.back || "Back"}
          title={t.trans?.back || "Back"}
        >
          <FiArrowLeft />
        </button>
        <h1 className="transaction-title">{t.trans?.title || "My Winis"}</h1>
        <button className="transaction-filter"  aria-label={t.trans?.filter || "Filter"} title={t.trans?.filter || "Filter"}>
          <FiFilter />
        </button>
      </div>

      {/* Transaction List */}
      <div className="transaction-list">
        {transactions.length === 0 ? (
          <div className="no-transactions">
            <p>{t.trans?.empty || "No transactions available"}</p>
          </div>
        ) : (
                     transactions.map((t) => (
             <div 
               key={t.id} 
               className="transaction-item"
               onClick={() => navigate(`/trans-details/${t.id}`)}
               style={{ cursor: 'pointer' }}
             >
               <div className="transaction-icon">
                 <img src={wintoCoin} alt="Wini Coin" />
               </div>

               <div className="transaction-details">
                 <div className="transaction-type">{t.source}</div>
                 <div className="transaction-date">{formatDateTime(t.time)}</div>
               </div>

               <div className="transaction-amount">
                 <div className={`amount ${t.isOutgoing ? 'negative' : 'positive'}`}>
                   {t.isOutgoing ? '-' : '+'} {asNumber(t.amount).toFixed(2)}
                 </div>
                 <div className="currency">winis</div>
               </div>
             </div>
           ))
        )}
      </div>
    </div>
  );
};

export default Transaction;
